import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import cn from 'classnames'
import { ROUTES } from 'common/constants'
import { BanIcon } from 'common/icons/BanIcon'
import { FileIcon } from 'common/icons/FileIcon'
import { HeadsetIcon } from 'common/icons/HeadsetIcon'
import { TrashIcon } from 'common/icons/TrashIcon'
import { Typography, TypographyVariants } from 'common/typography'
import { SectionType } from 'features/Settings/types'
import { selectSettingsTranslations } from 'features/Translations/selectors'
import styles from './styles.module.sass'

interface SettingsMenuProps {
  setIsDeleteModalOpen: (isOpen: boolean) => void
  isDeleteModalOpen: boolean
}

const sectionRouteMap: Record<string, SectionType> = {
  [`${ROUTES.SETTINGS}${ROUTES.SUPPORT}`]: SectionType.Support,
  [`${ROUTES.SETTINGS}${ROUTES.PRIVACY_POLICY}`]: SectionType.Privacy,
  [`${ROUTES.SETTINGS}${ROUTES.TERMS_OF_SERVICE}`]: SectionType.Terms,
  [`${ROUTES.SETTINGS}${ROUTES.CHILD_SAFETY_POLICY}`]: SectionType.ChildSafety,
  [`${ROUTES.SETTINGS}${ROUTES.BLOCKED_USERS}`]: SectionType.BlockedUsers
}

export const SettingsMenu = ({ setIsDeleteModalOpen, isDeleteModalOpen }: SettingsMenuProps) => {
  const history = useHistory()
  const location = useLocation()
  const settingsTranslations = useSelector(selectSettingsTranslations)
  const currentSection = sectionRouteMap[location.pathname] || SectionType.Support

  const menuItems = [
    {
      types: [SectionType.Support],
      label: settingsTranslations.settingsSidebarContactSupport,
      icon: HeadsetIcon,
      route: ROUTES.SUPPORT
    },
    {
      types: [SectionType.Privacy, SectionType.Terms, SectionType.ChildSafety],
      label: settingsTranslations.settingsSidebarPrivacyPolicy,
      icon: FileIcon,
      route: ROUTES.PRIVACY_POLICY
    },
    {
      types: [SectionType.BlockedUsers],
      label: settingsTranslations.settingsSidebarBlockedUsers,
      icon: BanIcon,
      route: ROUTES.BLOCKED_USERS
    },
    {
      types: [SectionType.DeleteData],
      label: settingsTranslations.settingsSidebarDeleteProfile,
      icon: TrashIcon,
      isDelete: true
    }
  ]

  const handleClick = (isDelete: boolean | undefined, route?: string) => {
    if (isDelete) {
      setIsDeleteModalOpen(true)
    } else if (route) {
      history.push(`${ROUTES.SETTINGS}${route}`)
    }
  }

  return (
    <div className={styles.menuContainer}>
      <Typography variant={TypographyVariants.Desktop_UI_XL_Medium} tag="span" className={styles.title}>
        {settingsTranslations.settingsSidebarTitle}
      </Typography>
      <div className={styles.menuItems}>
        {menuItems.map(({
          types, label, icon: Icon, isDelete, route
        }) => (
          <div
            key={types.join(',')}
            className={cn(
              styles.menuItem,
              types.includes(currentSection) && styles.active,
              isDeleteModalOpen && isDelete && styles.deleteActive
            )}
            onClick={() => handleClick(isDelete, route)}
          >
            <div className={styles.icon}><Icon /></div>
            <Typography variant={TypographyVariants.Desktop_UI_L_Medium} tag="span">
              {label}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  )
}
